import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '@/utils/env';
import '../styles/serviceSelect.css';

const LOGO_URL = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/Conncct-logo.png';

type Selection = 'incorporate' | 'operate';

const CheckIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>
);

function AccountingSelect() {
  const [selected, setSelected] = useState<Selection | null>(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  // Persist `accountingActivated` on the backend the moment the user commits to an
  // accounting path. This is the durable source of truth so that — after they later
  // activate consulting and switch back — accounting still shows as activated.
  const markAccountingActivated = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user?.uid) return;
      const tokensStr = localStorage.getItem('authTokens');
      const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
      fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        body: JSON.stringify({ accountingActivated: true }),
      }).catch(() => {});
    } catch { /* ignore */ }
  };

  const handleContinue = () => {
    if (!selected) return;
    localStorage.setItem('signupUserType', 'setup');
    localStorage.setItem('activeServiceView', 'accounting');
    markAccountingActivated();
    if (selected === 'incorporate') {
      localStorage.setItem('setupService', 'incorporate');
      navigate('/setup-business-type');
    } else {
      setShowModal(true);
    }
  };

  const handleOperateConfirm = () => {
    localStorage.setItem('setupService', 'operate');
    navigate('/dashboard', { replace: true });
  };

  const pick = (val: Selection) => setSelected(val);

  return (
    <div className="ss-page">
      <div className="ss-dots" aria-hidden="true" />

      <nav className="ss-nav">
        <a href="/" className="ss-logo">
          <img src={LOGO_URL} alt="Conncct" style={{ height: 32, width: 'auto' }} />
        </a>
        <div className="ss-nav-right">
          <a className="ss-login" href="/login">Log in</a>
        </div>
      </nav>

      <div className="ss-wrap">
        <header className="ss-head">
          <span className="ss-eyebrow">Get started</span>
          <h1>How can we help you today?</h1>
          <p>Select the service that best fits your current business stage. We'll guide you through the rest.</p>
        </header>

        <div className="ss-grid" role="radiogroup" aria-label="Choose a service">

          {/* Incorporate — blue */}
          <div
            className={`ss-opt${selected === 'incorporate' ? ' sel' : ''}`}
            role="radio"
            aria-checked={selected === 'incorporate'}
            tabIndex={0}
            onClick={() => pick('incorporate')}
            onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); pick('incorporate'); } }}
          >
            <span className="ss-radio"><CheckIcon /></span>
            <div className="ss-tile">
              <svg viewBox="0 0 24 24">
                <path d="M4.5 16.5c-1.5 1.3-2 5-2 5s3.7-.5 5-2c.7-.8.7-2 0-2.9a2 2 0 0 0-3 0Z"/>
                <path d="M12 15 9 12c.6-3 2.4-6 7-7 1 4.6-2 6.4-5 7Z"/>
                <path d="M9 12H5s.5-2.2 2-3c1.7-.9 3 0 3 0"/>
                <path d="M12 15v4s2.2-.5 3-2c.9-1.7 0-3 0-3"/>
              </svg>
            </div>
            <div className="ss-kicker">For launching</div>
            <h3>Incorporate</h3>
            <p className="ss-lede">You're launching. We set you up for scale.</p>
            <ul className="ss-feats">
              <li><span className="ss-feat-ic"><CheckIcon /></span>Company formation: UAE, USA &amp; global hubs</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>Licensing, tax ID &amp; corporate bank account</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>Structured for future fundraising from day one</li>
            </ul>
          </div>

          {/* Operate — gold */}
          <div
            className={`ss-opt gold${selected === 'operate' ? ' sel' : ''}`}
            role="radio"
            aria-checked={selected === 'operate'}
            tabIndex={0}
            onClick={() => pick('operate')}
            onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); pick('operate'); } }}
          >
            <span className="ss-radio"><CheckIcon /></span>
            <div className="ss-tile">
              <svg viewBox="0 0 24 24">
                <path d="M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v0H5a2 2 0 0 0-2 2Z"/>
                <rect x="3" y="7" width="18" height="13" rx="2.5"/>
                <path d="M16 12.5h3.5v3H16a1.5 1.5 0 0 1 0-3Z"/>
              </svg>
            </div>
            <div className="ss-kicker">For running</div>
            <h3>Operate</h3>
            <p className="ss-lede">You're running. We keep the back office institutional.</p>
            <ul className="ss-feats">
              <li><span className="ss-feat-ic"><CheckIcon /></span>Corporate Tax, VAT &amp; regulatory filings</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>Bookkeeping, payroll &amp; investor-grade reporting</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>Books always ready for diligence</li>
            </ul>
          </div>

        </div>

        <div className="ss-foot">
          <button className="ss-back" onClick={() => navigate(-1)}>← Back</button>
          <div className="ss-cont-wrap">
            {!selected && <span className="ss-hint">Select an option to continue</span>}
            <button className="ss-continue" disabled={!selected} onClick={handleContinue}>
              {selected === 'incorporate'
                ? 'Continue with Incorporate →'
                : selected === 'operate'
                ? 'Continue with Operate →'
                : 'Continue →'}
            </button>
          </div>
        </div>
      </div>

      {/* Operate confirmation modal */}
      {showModal && (
        <div className="ss-overlay" role="dialog" aria-modal="true" onClick={() => setShowModal(false)}>
          <div className="ss-modal" onClick={e => e.stopPropagation()}>
            <h3>Run &amp; grow your existing business</h3>
            <p>
              Please confirm that you already have a registered company with a Tax Registration Number (TRN) before proceeding.
              If you need to form a company first, go back and select <strong>Incorporate</strong>.
            </p>
            <div className="ss-modal-btns">
              <button className="ss-modal-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="ss-modal-confirm" onClick={handleOperateConfirm}>I have a company →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountingSelect;
